import { useCallback, useEffect, useRef } from 'react'

import axios from 'axios'
import { getItemFromLS } from 'utils/localStorage'
import merge from 'lodash/merge'
import qs from 'qs'
import { useAuth0 } from '@auth0/auth0-react'
import useInnovaAuth from './useInnovaAuth'

const request = async (options, getAccessTokenSilently, userName, databaseOrg, isAuthenticated) => {
  if (!isAuthenticated) {
    return {
      data: null,
      error: {
        response: {
          data: {
            error: 'User is not authenticated',
          },
        },
      },
    }
  }

  if (userName === '') {
    return {
      data: null,
      error: {
        response: {
          data: {
            error: 'userName is empty',
          },
        },
      },
    }
  }

  const accessToken = await getAccessTokenSilently()

  try {
    const inputData = options.data

    const data = qs.stringify(
      {
        ...inputData,
        userName,
        databaseOrg,
        productKey: process.env.REACT_APP_ICP_PRODUCT_KEY,
      },
      {
        allowDots: true,
        arrayFormat: 'comma',
      },
    )

    const axiosParams = {
      method: 'post',
      baseURL: process.env.REACT_APP_ICP_API,
      timeout: 60000,
      responseType: options.responseType,
      ...options,
      data,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    }

    const response = await axios(axiosParams)

    return {
      data: response.data,
      error: response.error,
      headers: response.headers,
    }
  } catch (error) {
    if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') {
      return {
        data: null,
        error: null,
        cancelled: true,
      }
    }

    return {
      data: null,
      error,
      cancelled: false,
    }
  }
}

const useInnovaAxios = (options) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0()
  const { user, getDatabaseOrg } = useInnovaAuth()

  const controllersRef = useRef(new Set())
  const optionsRef = useRef(options)

  // Keep the latest options available without changing fetch identity
  optionsRef.current = options

  useEffect(() => {
    const controllers = controllersRef.current

    return () => {
      controllers.forEach((controller) => controller.abort())
      controllers.clear()
    }
  }, [])

  const fetch = useCallback(
    async (payload, url, timeout) => {
      let userName = user?.name || ''

      if (!userName) {
        userName = getItemFromLS('userName', 'userName') || ''
      }

      const controller = new AbortController()
      controllersRef.current.add(controller)

      const requestOptions = {
        ...optionsRef.current,
        signal: controller.signal,
      }

      if (url) {
        requestOptions.url = url
      }

      if (timeout && typeof timeout === 'number') {
        requestOptions.timeout = timeout
      }

      const newOptions = merge({}, requestOptions, {
        data: payload,
      })

      try {
        return await request(newOptions, getAccessTokenSilently, userName, getDatabaseOrg(), isAuthenticated)
      } finally {
        controllersRef.current.delete(controller)
      }
    },
    [user?.name, getAccessTokenSilently, getDatabaseOrg, isAuthenticated],
  )

  return fetch
}

export default useInnovaAxios